var Component         = require("enyo/Component"),
    IntlMessageFormat = require("intl-messageformat").default,
    config            = require("app/config"),
    kind              = require("enyo/kind"),
    localeFilter      = require("bcp-47-match").basicFilter,
    localeParser      = require("bcp-47").parse,
    logger            = require("enyo/logger"),
    translationLoader = require("app/theme/translations/Loader");

module.exports = kind(/** @lends app.i18n.I18nService.prototype */ {

    /**
     * @private
     */
    name: "app.i18n.I18nService",

    /**
     * @private
     */
    kind: Component,

    /**
     * The current locale
     *
     * @type  {?string}
     * @public
     */
    locale: null,

    /**
     * The current language
     *
     * @type  {?string}
     * @readonly
     * @public
     */
    language: null,

    /**
     * The regular expression used to extract a locale from the url
     *
     * @type  {RegExp}
     * @public
     */
    urlLocaleRegex: /^\/([a-z]{2})/,

    /**
     * A promise that resolves when translations have loaded.
     *
     * @type  {Promise}
     * @readonly
     * @public
     */
    translationPromise: null,

    /**
     * Translations
     */
    translation: {},

    /**
     * @type {?Object<Intl.RelativeTimeFormat>}
     * @private
     */
    relativeTimeFormatter: null,

    /**
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.relativeTimeFormatter = {
                long:   null,
                short:  null,
                narrow: null
            };
        };
    }),

    /**
     * Resolve the locale by trying out various sources until a locale is
     * resolved.
     *
     * @param    {string}   path  The url path
     * @param    {Object}   user  The user
     * @returns  {Promise}  A promise that resolves when the new translations
     *                      have loaded.
     * @public
     */
    resolveLocale: function(path, user) {
        var locale;

        // Attempt to resolve which language to use.
        locale = (path && this.getLocaleFromUrlPath(path))
            || (user && this.getLocaleFromUser(user))
            || this.getLocaleFromBrowser()
            || this.getDefaultLocale();

        this.set("locale", locale);

        return this.get("translationPromise") || Promise.resolve();
    },

    /**
     * Attempt to let a url path tell us which locale to use. If no supported
     * locale is found, this method will return `null`.
     *
     * @param    {string}   path  The url path
     * @returns  {?string}  The locale if found, `null` otherwise.
     * @public
     */
    getLocaleFromUrlPath: function(path) {
        var match,
            urlLocale,
            supportedLocales;

        // Search for locale in url
        match = path.match(this.urlLocaleRegex);

        // No locale found
        if (!match) {
            return null;
        }

        // A locale was found, check to see if it is supported
        urlLocale        = match[1];
        supportedLocales = localeFilter(config.supportedLocales, [urlLocale]);

        return supportedLocales[0] || null;
    },

    /**
     * Return the locale for the current user, if no supported language is
     * present this method will return `null`.
     *
     * @param    {Object}   user  The user
     * @returns  {?string}  The locale if found, `null` otherwise.
     * @public
     */
    getLocaleFromUser: function(user) {
        var language = user.language || "",
            supportedLocales;

        // Reduce to those supported by our app
        supportedLocales = localeFilter(config.supportedLocales, [language]);

        return supportedLocales[0] || null;
    },

    /**
     * Attempt to let the browser tell us which locale to use. If no supported
     * locale is found, this method will return `null`.
     *
     * @returns  {?string}  The locale if found, `null` otherwise.
     * @public
     */
    getLocaleFromBrowser: function() {
        var preferredLocales,
            supportedLocales;

        // Get preferred locales from the browser
        preferredLocales = navigator.languages
            ? navigator.languages
            : [navigator.language || navigator.userLanguage];

        // Reduce to those supported by our app
        supportedLocales = localeFilter(config.supportedLocales, preferredLocales);

        return supportedLocales[0] || null;
    },

    /**
     * Get the default locale from the config.
     *
     * @returns  {string}  The locale
     * @public
     */
    getDefaultLocale: function() {
        return config.defaultLocale;
    },

    /**
     * Get the default language from the config.
     *
     * @returns  {string}  The language
     * @public
     */
    getDefaultLanguage: function() {
        return this.convertLocaleToLanguage(this.getDefaultLocale());
    },

    /**
     * @param    {string}  locale    The locale
     * @returns  {string}  language  The language
     * @public
     */
    convertLocaleToLanguage: function(locale) {
        return localeParser(locale.replace("_", "-")).language;
    },

    /**
     * Update language selection based on new locale.
     *
     * @private
     */
    localeChanged: function() {
        var locale = this.get("locale"),
            language;

        if (locale) {
            language = localeParser(locale.replace("_", "-")).language;
        }

        if (language) {
            this.set("language", language);
            this.set("relativeTimeFormatter", {
                long:   null,
                short:  null,
                narrow: null
            });
            this.loadTranslation(language);
        }
        else {
            this.set("language", null);
        }
    },

    /**
     * Load a new translation pack.
     *
     * @param  {string}  language  The desired language
     * @private
     */
    loadTranslation: function(language) {
        var promise;

        promise = translationLoader.load(language, this);
        promise.catch(this.bindSafely(function(error) {
            logger.error("Cannot load translations for language: `" + language + "`");
            logger.error(error);

            if (typeof Sentry !== "undefined" && error instanceof Error) {
                Sentry.captureException(error, {
                    extra: {
                        language: language
                    }
                });
            }

            return Promise.resolve();
        }));

        this.set("translationPromise", promise);
    },

    /**
     * Used to retrieve translations that aren't strings. Such as translation
     * data for 3rd party components.
     *
     * @param    {string}   name  The name at which the translation is stored
     * @returns  {?string}  The translation
     * @public
     */
    getTranslation: function(name) {
        return this.translation[name] || null;
    },

    /**
     * @param    {string}   key      The key at which the string is stored
     * @param    {Object}   options  The formatting options
     * @returns  {?string}  The translation string
     * @public
     */
    getString: function(key, options) {
        var string;

        string = this.translation[key] || null;

        if (string && options) {
            try {
                return new IntlMessageFormat(string, this.get("locale")).format(options);
            }
            catch (error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error, {
                        extra: {
                            key:     key,
                            options: options
                        }
                    });
                }

                return null;
            }
        }

        return string;
    },

    /**
     * Get a relative time formatter for the requested style.
     *
     * @param    {string}                   style  The formatting style
     * @returns  {Intl.RelativeTimeFormat}  The formatter.
     * @public
     */
    getRelativeTimeFormatter: function(style) {
        if (!this.relativeTimeFormatter[style]) {
            this.relativeTimeFormatter[style] = new Intl.RelativeTimeFormat(this.get("locale"), {
                localeMatcher: "best fit",
                numeric:       "always",
                style:         style
            });
        }

        return this.relativeTimeFormatter[style];
    },

    /**
     * Translate a provided path to another language. If no language is
     * provided, the url path will be translated to the current language.
     *
     * @param    {string}   path      The url path
     * @param    {?string}  language  Optionally provided language
     * @returns  {string}   The translated path if a compatible language was
     *                      found, the original path otherwise.
     * @public
     */
    translateUrlPath: function(path, language) {
        var urlLocaleRegex = this.urlLocaleRegex,
            supportedLocales;

        // Reduce to supported locales
        language         = language || this.get("language");
        supportedLocales = localeFilter(config.supportedLocales, [language]);
        language         = supportedLocales[0] || null;

        return language ? path.replace(urlLocaleRegex, "/" + language) : path;
    },

    /**
     * Translate a provided path to all currently supported languages.
     *
     * @param    {string}  path  The url path
     * @returns  {Object}  An object with language->path pairs for all supported
     *                     locales.
     * @public
     */
    getAllUrlPathTranslations: function(path) {
        var urlLocaleRegex = this.urlLocaleRegex,
            translations   = {};

        config.supportedLocales.forEach(function(locale) {
            var language           = localeParser(locale.replace("_", "-")).language;
            translations[language] = path.replace(urlLocaleRegex, "/" + language);
        });

        return translations;
    }

});
